"use client";

/**
 * Formulaire de capture de lead PARTENAIRE (B2B apporteurs d'affaires).
 *
 * Surface de conversion à plus forte valeur du silo /chauffeur/partenaires :
 * conciergeries, hôtels et agences. Le lead est capturé SUR la page (zéro
 * friction) — plus de simple lien vers /contact.
 *
 * Fin wrapper au-dessus de <LeadForm> (coquille + pipeline d'envoi PARTAGÉS) :
 * validation client (react-hook-form), honeypot anti-spam, locale dans le body,
 * POST vers /api/contact, états loading / success / error. Aucune nouvelle
 * mécanique de fetch. Seuls les champs (type/établissement/ville/volume +
 * coordonnées), le sujet/message et la charge utile partenaire (companyName/
 * budget) sont fournis ici ; tout le reste est mutualisé.
 *
 * Bilingue via la prop `locale` (défaut "fr"). Toutes les chaînes visibles
 * viennent de PARTNER_UI[locale].form. Les taux de commission affichés sont lus
 * depuis app/config/partners.ts (via les options de volume) — jamais en dur.
 *
 * Drapeau partenaire : locationType = "partenariat" (détecté côté API pour
 * préfixer le sujet équipe). Ancre de section : id="partenariat".
 */
import { useMemo } from "react";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { PHONE_PATTERNS } from "@/config/forms";
import LeadForm, {
  LEAD_INPUT_CLASS,
  LEAD_LABEL_CLASS,
} from "@/components/forms/LeadForm";
import { PARTNER_UI, PARTNER_LEAD_FORM_ID } from "./partnerUi";

type PartnerLeadFormData = {
  type: string;
  establishmentName: string;
  city: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  volume: string;
  message: string;
  honeypot?: string;
};

const PARTNER_LEAD_DEFAULTS: PartnerLeadFormData = {
  type: "",
  establishmentName: "",
  city: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  volume: "",
  message: "",
  honeypot: "",
};

export default function PartnerLeadForm({
  locale = "fr",
}: {
  locale?: Locale;
}): JSX.Element {
  const ui = tr(PARTNER_UI, locale).form;
  // Libellés FR des types (l'équipe lit le FR) — pour composer un sujet clair
  // quelle que soit la langue du visiteur.
  const frTypeLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of PARTNER_UI.fr.form.typeOptions) {
      map.set(option.value, option.label);
    }
    return map;
  }, []);

  const inputClass = LEAD_INPUT_CLASS;
  const labelClass = LEAD_LABEL_CLASS;

  return (
    <LeadForm<PartnerLeadFormData>
      locale={locale}
      anchorId={PARTNER_LEAD_FORM_ID}
      submitIcon="mdi:handshake-outline"
      honeypotId="partner-honeypot"
      defaultValues={PARTNER_LEAD_DEFAULTS}
      resetValues={PARTNER_LEAD_DEFAULTS}
      ui={{
        eyebrow: ui.eyebrow,
        heading: ui.heading,
        subtitle: ui.subtitle,
        honeypotLabel: ui.honeypotLabel,
        submit: ui.submit,
        sending: ui.sending,
        successMessage: ui.successMessage,
        errorMessage: ui.errorMessage,
        consentNote: ui.consentNote,
        privacyNote: ui.privacyNote,
      }}
      buildSubmission={(values) => {
        const typeLabelFr =
          frTypeLabels.get(values.type) || frTypeLabels.get("autre") || "Autre";
        const typeLabelLocale =
          ui.typeOptions.find((option) => option.value === values.type)
            ?.label || typeLabelFr;
        const establishmentName = values.establishmentName.trim();
        const city = values.city.trim();

        // Sujet équipe (FR) : concis et lisible — type + établissement.
        const subject = `Partenariat — ${typeLabelFr} — ${
          establishmentName || "établissement"
        }`;

        // Message : saisie libre, sinon défaut sensé (≥ 5 car. requis côté API).
        // La ville est repliée dans le message pour que l'équipe la voie.
        const typedMessage = values.message.trim();
        const composedDefault = ui.defaultMessage({
          type: typeLabelLocale,
          establishment: establishmentName,
          city,
        });
        const message =
          typedMessage.length >= 5
            ? city
              ? `${typedMessage}\n${ui.cityLabel}: ${city}`
              : typedMessage
            : composedDefault;

        return {
          subject,
          message,
          locationType: "partenariat",
          extraPayload: {
            companyName: establishmentName || undefined,
            budget: values.volume || undefined,
          },
        };
      }}
      fields={(register, errors, watch) => {
        // Commission estimée selon la fourchette de volume choisie (lue des tiers).
        const selectedVolume = watch("volume");
        const selectedVolumeOption = ui.volumeOptions.find(
          (option) => option.value === selectedVolume,
        );

        return (
          <>
            {/* Type d'établissement */}
            <label className="block">
              <span className={labelClass}>{ui.typeLabel}</span>
              <select
                className={inputClass}
                {...register("type", { required: ui.requiredType })}
              >
                <option value="">{ui.typePlaceholder}</option>
                {ui.typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.type && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.type.message}
                </span>
              )}
            </label>

            {/* Nom de l'établissement */}
            <label className="block">
              <span className={labelClass}>{ui.establishmentLabel}</span>
              <input
                className={inputClass}
                placeholder={ui.establishmentPlaceholder}
                {...register("establishmentName", {
                  required: ui.requiredEstablishment,
                })}
              />
              {errors.establishmentName && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.establishmentName.message}
                </span>
              )}
            </label>

            {/* Ville / localisation */}
            <label className="block">
              <span className={labelClass}>{ui.cityLabel}</span>
              <input
                className={inputClass}
                placeholder={ui.cityPlaceholder}
                {...register("city")}
              />
            </label>

            {/* Volume / CA annuel estimé */}
            <label className="block">
              <span className={labelClass}>{ui.volumeLabel}</span>
              <select className={inputClass} {...register("volume")}>
                <option value="">{ui.volumePlaceholder}</option>
                {ui.volumeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedVolumeOption?.rate != null && (
                <span className="mt-1 block text-xs font-medium text-primary">
                  {ui.volumeTierHint(selectedVolumeOption.rate)}
                </span>
              )}
            </label>

            {/* Prénom */}
            <label className="block">
              <span className={labelClass}>{ui.firstNameLabel}</span>
              <input
                className={inputClass}
                placeholder={ui.firstNamePlaceholder}
                {...register("firstName", {
                  required: ui.requiredFirstName,
                  minLength: { value: 2, message: ui.requiredFirstName },
                })}
              />
              {errors.firstName && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.firstName.message}
                </span>
              )}
            </label>

            {/* Nom */}
            <label className="block">
              <span className={labelClass}>{ui.lastNameLabel}</span>
              <input
                className={inputClass}
                placeholder={ui.lastNamePlaceholder}
                {...register("lastName", {
                  required: ui.requiredLastName,
                  minLength: { value: 2, message: ui.requiredLastName },
                })}
              />
              {errors.lastName && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.lastName.message}
                </span>
              )}
            </label>

            {/* Email */}
            <label className="block">
              <span className={labelClass}>{ui.emailLabel}</span>
              <input
                type="email"
                className={inputClass}
                placeholder={ui.emailPlaceholder}
                {...register("email", { required: ui.requiredEmail })}
              />
              {errors.email && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.email.message}
                </span>
              )}
            </label>

            {/* Téléphone (international, facultatif) */}
            <label className="block">
              <span className={labelClass}>{ui.phoneLabel}</span>
              <input
                type="tel"
                className={inputClass}
                placeholder={ui.phonePlaceholder}
                {...register("phone", {
                  pattern: {
                    value: PHONE_PATTERNS.intl,
                    message: ui.invalidPhone,
                  },
                })}
              />
              {errors.phone && (
                <span className="mt-1 block text-xs text-red-600">
                  {errors.phone.message}
                </span>
              )}
            </label>

            {/* Message (facultatif) */}
            <label className="block md:col-span-2">
              <span className={labelClass}>{ui.messageLabel}</span>
              <textarea
                rows={4}
                maxLength={900}
                className="w-full resize-y rounded-lg border border-black/15 bg-white px-3 py-2.5 text-sm text-black outline-none ring-primary/40 transition focus:ring-2"
                placeholder={ui.messagePlaceholder}
                {...register("message")}
              />
            </label>
          </>
        );
      }}
    />
  );
}
